import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormHelperText,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material"
import { Paperclip } from "lucide-react"
import { useEffect, useRef, useState, type JSX } from "react"

import { useFinancialAccess } from "@crm-src/components/money"
import { useResellerAccess } from "@crm-src/components/reseller"
import { ResellerSelector } from "@crm-src/components/reseller-selector"
import {
  purchaseOrdersApi,
  type PurchaseOrder,
  type PurchaseOrderPatch,
} from "@crm-src/lib/purchase-orders-api"

import { CustomerSearchPicker, type CustomerPick } from "./customer-search-picker"
import {
  centsToDollarsInput,
  dollarsToCents,
  formatBytes,
  PURCHASE_ORDER_ACCEPT,
  todayIso,
  validatePurchaseOrderForm,
  type PurchaseOrderFormErrors,
  type PurchaseOrderFormValues,
} from "./purchase-order-format"

export interface PurchaseOrderDialogProps {
  open: boolean
  onClose: () => void
  /** Present ⇒ EDIT this purchase order; absent ⇒ UPLOAD a new one. */
  purchaseOrder?: PurchaseOrder | null
  /** Pre-assign a new upload (from a customer or reseller profile). */
  defaultCustomer?: CustomerPick | null
  defaultResellerId?: string | null
  onSaved: (po: PurchaseOrder) => void
}

function initialValues(
  po: PurchaseOrder | null | undefined,
  defaultCustomer: CustomerPick | null | undefined,
  defaultResellerId: string | null | undefined,
): { values: PurchaseOrderFormValues; customer: CustomerPick | null } {
  if (po) {
    return {
      customer: po.customer ? { id: String(po.customer.id), name: po.customer.company_name } : null,
      values: {
        file: null,
        customerId: po.customer_id ? String(po.customer_id) : null,
        resellerId: po.reseller_id ? String(po.reseller_id) : null,
        poNumber: po.po_number ?? "",
        orderDate: po.order_date ?? "",
        total: centsToDollarsInput(po.total_cents),
        note: po.note ?? "",
      },
    }
  }
  return {
    customer: defaultCustomer ?? null,
    values: {
      file: null,
      customerId: defaultCustomer?.id ?? null,
      resellerId: defaultResellerId ?? null,
      poNumber: "",
      orderDate: todayIso(),
      total: "",
      note: "",
    },
  }
}

/**
 * Upload a purchase order that arrived by email — or edit one — as XOP staff.
 *
 * ONE dialog for both, because the fields are the same: the buyer's PO number,
 * the order date, WHO it belongs to (a customer and/or a reseller — at least
 * one, which crm-api also enforces), the total and a note. Only an upload takes
 * a file here; replacing the document on an existing purchase order is its own
 * action on the detail page.
 *
 * The walls: the reseller picker is absent without reseller access (and the
 * edit then never sends `reseller_id`, so it cannot clear an assignment the
 * viewer cannot see); the total field is absent without financial access (and
 * `total_cents` is then never sent).
 */
export function PurchaseOrderDialog({
  open,
  onClose,
  purchaseOrder,
  defaultCustomer,
  defaultResellerId,
  onSaved,
}: PurchaseOrderDialogProps): JSX.Element {
  const editing = !!purchaseOrder
  const resellerAccess = useResellerAccess()
  const financialAccess = useFinancialAccess()

  const [values, setValues] = useState<PurchaseOrderFormValues>(
    () => initialValues(purchaseOrder, defaultCustomer, defaultResellerId).values,
  )
  const [customer, setCustomer] = useState<CustomerPick | null>(
    () => initialValues(purchaseOrder, defaultCustomer, defaultResellerId).customer,
  )
  const [errors, setErrors] = useState<PurchaseOrderFormErrors>({})
  const [serverError, setServerError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const fileInput = useRef<HTMLInputElement | null>(null)

  // Every opening starts from the record (or a blank upload), never from the
  // half-typed state of the last time the dialog was closed.
  useEffect(() => {
    if (!open) return
    const initial = initialValues(purchaseOrder, defaultCustomer, defaultResellerId)
    setValues(initial.values)
    setCustomer(initial.customer)
    setErrors({})
    setServerError(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, purchaseOrder?.id])

  const set = <K extends keyof PurchaseOrderFormValues>(key: K, value: PurchaseOrderFormValues[K]) =>
    setValues((previous) => ({ ...previous, [key]: value }))

  const submit = async () => {
    const found = validatePurchaseOrderForm(values, {
      requireFile: !editing,
      requireAssignment: resellerAccess || !editing,
    })
    setErrors(found)
    if (Object.keys(found).length > 0) return

    const fields: PurchaseOrderPatch = {
      po_number: values.poNumber.trim() || null,
      order_date: values.orderDate || null,
      customer_id: values.customerId,
      note: values.note.trim() || null,
      ...(resellerAccess ? { reseller_id: values.resellerId } : {}),
      ...(financialAccess ? { total_cents: dollarsToCents(values.total) } : {}),
    }

    setSaving(true)
    setServerError(null)
    try {
      const saved =
        editing && purchaseOrder
          ? await purchaseOrdersApi.update(purchaseOrder.id, fields)
          : await purchaseOrdersApi.create(fields, values.file as File)
      onSaved(saved)
    } catch (err: any) {
      const data = err?.response?.data
      setServerError(
        data?.details?.issues?.join(" ") || data?.errors?.join(" ") || data?.error || "The purchase order could not be saved.",
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" data-cy="po-dialog">
      <DialogTitle>{editing ? `Edit ${purchaseOrder?.number}` : "Upload purchase order"}</DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 2.5 }}>
        {!editing && (
          <DialogContentText>
            A purchase order that reached XOP by email. Attach the buyer&apos;s file and say who it belongs to — a
            {resellerAccess ? "customer, a reseller, or both" : "customer"}.
          </DialogContentText>
        )}

        {serverError && (
          <Alert severity="error" data-cy="po-dialog-error">
            {serverError}
          </Alert>
        )}

        {!editing && (
          <Box>
            <input
              ref={fileInput}
              type="file"
              hidden
              accept={PURCHASE_ORDER_ACCEPT}
              data-cy="po-file-input"
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null
                set("file", file)
                setErrors((previous) => {
                  const { file: _dropped, ...rest } = previous
                  return rest
                })
              }}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <Button
                variant="outlined"
                startIcon={<Paperclip size={16} />}
                onClick={() => fileInput.current?.click()}
                data-cy="po-choose-file"
              >
                {values.file ? "Choose another file" : "Choose file"}
              </Button>
              {values.file ? (
                <Typography sx={{ fontSize: 14 }} data-cy="po-file-name">
                  {values.file.name}{" "}
                  <Box component="span" sx={{ color: "text.secondary" }}>
                    {formatBytes(values.file.size)}
                  </Box>
                </Typography>
              ) : (
                <Typography sx={{ fontSize: 14, color: "text.secondary" }}>PDF, image, Word or Excel · up to 10 MB</Typography>
              )}
            </Box>
            {errors.file && (
              <FormHelperText error data-cy="po-file-error">
                {errors.file}
              </FormHelperText>
            )}
          </Box>
        )}

        <Box sx={{ display: "grid", gap: 1.5 }}>
          <CustomerSearchPicker
            value={customer}
            error={!!errors.assignment}
            onChange={(next) => {
              setCustomer(next)
              set("customerId", next?.id ?? null)
            }}
          />
          {resellerAccess && (
            <Box data-cy="po-reseller-picker">
              <ResellerSelector
                value={values.resellerId}
                onValueChange={(next) => set("resellerId", next)}
                showLabel
                label="Reseller"
                placeholder="No reseller"
              />
            </Box>
          )}
          {errors.assignment ? (
            <FormHelperText error data-cy="po-assignment-error">
              {errors.assignment}
            </FormHelperText>
          ) : (
            <FormHelperText>
              {resellerAccess ? "Assign a customer, a reseller, or both." : "Assign the customer it belongs to."}
            </FormHelperText>
          )}
        </Box>

        <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
          <TextField
            size="small"
            label="Buyer's PO number"
            value={values.poNumber}
            onChange={(e) => set("poNumber", e.target.value)}
            inputProps={{ "data-cy": "po-number-input", maxLength: 100 }}
          />
          <TextField
            size="small"
            type="date"
            label="Order date"
            value={values.orderDate}
            onChange={(e) => set("orderDate", e.target.value)}
            InputLabelProps={{ shrink: true }}
            error={!!errors.orderDate}
            helperText={errors.orderDate}
            inputProps={{ "data-cy": "po-order-date-input" }}
          />
          {financialAccess && (
            <TextField
              size="small"
              label="Total"
              value={values.total}
              onChange={(e) => set("total", e.target.value)}
              error={!!errors.total}
              helperText={errors.total ?? "Optional"}
              inputProps={{ "data-cy": "po-total-input", inputMode: "decimal" }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            />
          )}
        </Box>

        <TextField
          size="small"
          label="Note"
          value={values.note}
          onChange={(e) => set("note", e.target.value)}
          multiline
          minRows={2}
          inputProps={{ "data-cy": "po-note-input" }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={saving}
          data-cy="po-dialog-save"
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {editing ? "Save changes" : "Upload"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
